import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AppLayout } from '../components/layout/AppLayout';
import { db, storage } from '../lib/firebase';
import { doc, getDoc, collection, getDocs, query, where, orderBy, serverTimestamp, addDoc, updateDoc, deleteDoc, deleteField } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { PFISymbol, BrandGradient } from '../components/common/PFILogo';
import { formatProjectName } from '../utils/projectUtils';
import { formatDate } from '../utils/dateFormatters';
import SmartQuoteWizard from '../components/SmartQuoteWizard';
import CommunicationsInbox from '../components/CommunicationsInbox';
import FlamingoLoader from '../components/FlamingoLoader';
import CustomerGanttChart from '../components/CustomerGanttChart';
import MilestoneManager from '../components/MilestoneManager';
import { 
  ArrowLeftIcon,
  CalendarIcon,
  UserIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  ClipboardDocumentListIcon,
  PlusIcon,
  CheckCircleIcon,
  PhotoIcon,
  TrashIcon,
  PencilIcon,
  XMarkIcon,
  FlagIcon
} from '@heroicons/react/24/outline';

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [photos, setPhotos] = useState([]);
  const [componentPhotos, setComponentPhotos] = useState({});
  const [fabricPhotos, setFabricPhotos] = useState({});
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProject, setEditedProject] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showComponentWizard, setShowComponentWizard] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);
  const [newComponentName, setNewComponentName] = useState('');
  const [currentComponentId, setCurrentComponentId] = useState(null);
  const [showNameModal, setShowNameModal] = useState(false);
  const [uploadingFabricPhotos, setUploadingFabricPhotos] = useState({});
  const [uploadingComponentPhotos, setUploadingComponentPhotos] = useState({});
  const [milestones, setMilestones] = useState([]);
  const [showMilestoneManager, setShowMilestoneManager] = useState(false);
  const [showFlamingoLoader, setShowFlamingoLoader] = useState(false);
  const [flamingoMessage, setFlamingoMessage] = useState('');
  const [flamingoKey, setFlamingoKey] = useState(0);
  const [dragOverStates, setDragOverStates] = useState({});
  const fileUploadRef = useRef(null);

  useEffect(() => {
    loadProject();
  }, [id]);

  const loadProject = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, `projects/${id}`);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const projectData = {
          id: docSnap.id,
          ...docSnap.data()
        };
        setProject(projectData);
        
        // Initialize photos state from component data
        const fabricPhotosInit = {};
        const componentPhotosInit = {};
        projectData.components?.forEach(component => {
          // Handle fabric photos - check both old and new structures
          const fabricUrls = [];
          if (component.taxonomyData?.fabric?.imageUrls) {
            fabricUrls.push(...component.taxonomyData.fabric.imageUrls);
          }
          if (component.taxonomyData?.fabricPhotos?.imageUrls) {
            fabricUrls.push(...component.taxonomyData.fabricPhotos.imageUrls);
          }
          if (fabricUrls.length > 0) {
            fabricPhotosInit[component.id] = fabricUrls;
          }
          
          // Handle component photos
          if (component.taxonomyData?.componentPhotos?.imageUrls) {
            componentPhotosInit[component.id] = component.taxonomyData.componentPhotos.imageUrls;
          }
        });
        setFabricPhotos(fabricPhotosInit);
        setComponentPhotos(componentPhotosInit);
        
        // Load milestones
        if (projectData.milestones) {
          console.log('Loading milestones from Firestore:', projectData.milestones);
          setMilestones(projectData.milestones);
        } else {
          console.log('No milestones in project data');
          setMilestones([]);
        }
        
        // Load project photos and tasks
        await loadProjectPhotos();
        await loadProjectTasks();
      } else {
        setError('Project not found');
      }
    } catch (error) {
      console.error('Error loading project:', error);
      setError('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (files) => {
    if (!files.length) return;
    
    try {
      setUploadingPhotos(true);
      setFlamingoKey(Date.now()); // Unique key for new dance
      setFlamingoMessage(`Uploading ${files.length} gorgeous project photo${files.length > 1 ? 's' : ''}...`);
      setShowFlamingoLoader(true);
      console.log('Starting photo upload for files:', files);
      
      const uploadPromises = files.map(async (file) => {
        try {
          // Validate file object
          if (!file || !file.name) {
            console.warn('Invalid file object:', file);
            return null;
          }
          
          console.log('Processing file:', file.name, 'Size:', file.size, 'Type:', file.type);
          
          // Generate unique filename
          const timestamp = Date.now();
          const fileExtension = file.name.split('.').pop() || 'jpg';
          const filename = `${timestamp}_${Math.random().toString(36).substring(7)}.${fileExtension}`;
          const path = `projects/${id}/photos/${filename}`;
          
          console.log('Upload path:', path);

          // Upload file to Firebase Storage
          const storageRef = ref(storage, path);
          const uploadTask = uploadBytesResumable(storageRef, file);

          return new Promise((resolve, reject) => {
            uploadTask.on(
              'state_changed',
              null, // progress callback (optional)
              (error) => reject(error),
              async () => {
                try {
                  const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                  
                  // Save photo metadata to Firestore
                  const photoData = {
                    projectId: id,
                    url: downloadURL,
                    filename,
                    caption: '',
                    uploadedBy: 'user@example.com', // TODO: get from auth context
                    uploadedAt: new Date(),
                    type: 'reference'
                  };

                  const photoDoc = await addDoc(collection(db, 'projectPhotos'), photoData);
                  resolve(photoDoc.id);
                } catch (error) {
                  reject(error);
                }
              }
            );
          });
        } catch (error) {
          console.error('Failed to upload photo:', error);
          alert(`Failed to upload ${file.name}: ${error.message}`);
          return null;
        }
      });
      
      const results = await Promise.all(uploadPromises);
      const successfulUploads = results.filter(result => result !== null);
      
      // Reload photos after upload
      // Clear the file upload preview
      if (fileUploadRef.current) {
        fileUploadRef.current.value = '';
      }
      
      // Show success message with flamingo celebration
      setFlamingoMessage(`🎉 Successfully uploaded ${uploadPromises.length} project photo${uploadPromises.length > 1 ? 's' : ''}!`);
      setTimeout(() => {
        setShowFlamingoLoader(false);
        alert('Photos uploaded successfully!');
      }, 1500); // Show success for 1.5 seconds
    } catch (error) {
      console.error('Error uploading photos:', error);
      setShowFlamingoLoader(false);
      alert('Failed to upload photos');
    } finally {
      setUploadingPhotos(false);
    }
  };

  // Helper function to upload fabric photos to Firebase Storage
  const uploadFabricPhotos = async (photos, componentId) => {
    console.log('uploadFabricPhotos called with:', { photosCount: photos.length, componentId });
    
    const uploadPromises = photos.map(async (file, index) => {
      const fileName = `fabric_${Date.now()}_${index}_${file.name}`;
      const path = `projects/${id}/components/${componentId}/fabric/${fileName}`;
      
      console.log(`Uploading file ${index + 1}:`, { fileName, path, fileSize: file.size });
      
      const storageRef = ref(storage, path);
      const uploadTask = uploadBytesResumable(storageRef, file);
      
      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log(`Upload progress for ${fileName}: ${progress}%`);
          },
          (error) => {
            console.error(`Upload error for ${fileName}:`, error);
            reject(error);
          },
          async () => {
            try {
              console.log(`Upload complete for ${fileName}, getting download URL...`);
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              console.log(`Download URL obtained for ${fileName}:`, downloadURL);
              resolve({
                url: downloadURL,
                fileName: file.name,
                path,
                uploadedAt: new Date()
              });
            } catch (error) {
              console.error(`Error getting download URL for ${fileName}:`, error);
              reject(error);
            }
          }
        );
      });
    });
    
    console.log('Waiting for all uploads to complete...');
    const results = await Promise.all(uploadPromises);
    console.log('All uploads complete:', results);
    return results;
  };

  // Helper function to upload component photos to Firebase Storage
  const uploadComponentPhotos = async (photos, componentId) => {
    const uploadPromises = photos.map(async (file, index) => {
      const fileName = `component_${Date.now()}_${index}_${file.name}`;
      const path = `projects/${id}/components/${componentId}/${fileName}`;
      
      const storageRef = ref(storage, path);
      const uploadTask = uploadBytesResumable(storageRef, file);
      
      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          null,
          (error) => reject(error),
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve({
                url: downloadURL,
                fileName: file.name,
                path,
                uploadedAt: new Date()
              });
            } catch (error) {
              reject(error);
            }
          }
        );
      });
    });
    
    return Promise.all(uploadPromises);
  };

  // Handle fabric photo upload
  const handleFabricPhotoUpload = async (files, componentId) => {
    console.log('=== FABRIC UPLOAD DEBUG START ===');
    console.log('Files:', files);
    console.log('Component ID:', componentId);
    console.log('Project ID:', id);
    
    // Emergency timeout to prevent infinite loading
    let emergencyTimeout;
    
    try {
      console.log('1. Setting upload state...');
      setUploadingFabricPhotos(prev => ({ ...prev, [componentId]: true }));
      setFlamingoKey(Date.now()); // Unique key for new dance
      setFlamingoMessage(`Uploading ${files.length} stunning fabric photo${files.length > 1 ? 's' : ''}...`);
      setShowFlamingoLoader(true);
      
      console.log('2. Setting emergency timeout (60s)...');
      emergencyTimeout = setTimeout(() => {
        console.error('EMERGENCY TIMEOUT TRIGGERED: Hiding flamingo loader after 60 seconds');
        setShowFlamingoLoader(false);
        setUploadingFabricPhotos(prev => ({ ...prev, [componentId]: false }));
      }, 60000);
      
      // Validate inputs
      console.log('3. Validating inputs...');
      if (!files || !Array.isArray(files) || files.length === 0) {
        throw new Error('No valid files provided');
      }
      if (!componentId) {
        throw new Error('Component ID is required');
      }
      console.log('3a. Validation passed');
      
      console.log('4. Calling uploadFabricPhotos function...');
      const uploadedPhotos = await uploadFabricPhotos(files, componentId);
      console.log('5. Upload completed, returned photos:', uploadedPhotos);
      
      // Update the component's fabric photos in Firestore
      console.log('6. Starting Firestore update...');
      console.log('6a. Getting project document...');
      const projectRef = doc(db, `projects/${id}`);
      const projectDoc = await getDoc(projectRef);
      console.log('6b. Project doc exists?', projectDoc.exists());
      
      if (projectDoc.exists()) {
        console.log('7. Project found, getting components...');
        const projectData = projectDoc.data();
        const components = projectData.components || [];
        console.log('7a. Total components:', components.length);
        
        console.log('8. Finding component with ID:', componentId);
        const componentIndex = components.findIndex(c => String(c.id) === String(componentId));
        console.log('8a. Component found at index:', componentIndex);
        
        if (componentIndex !== -1) {
          console.log('9. Component found, extracting current photos...');
          const component = components[componentIndex];
          console.log('9a. Current component:', component);
          
          const currentFabricPhotos = component.taxonomyData?.fabricPhotos || { imageUrls: [], imageCount: 0 };
          
          const updatedFabricPhotos = {
            imageUrls: [...(currentFabricPhotos.imageUrls || []), ...uploadedPhotos.map(photo => photo.url)],
            imageCount: (currentFabricPhotos.imageCount || 0) + uploadedPhotos.length
          };
          
          components[componentIndex] = {
            ...component,
            taxonomyData: {
              ...component.taxonomyData,
              fabricPhotos: updatedFabricPhotos
            }
          };
          
          console.log('12. Updating project document...');
          await updateDoc(projectRef, {
            components: components,
            updatedAt: serverTimestamp()
          });
          console.log('13. Firestore update complete!');
          
          // Update the local state instead of reloading the entire project
          console.log('14. Updating local state...');
          setFabricPhotos(prev => ({
            ...prev,
            [componentId]: updatedFabricPhotos.imageUrls
          }));
          
          // Also update the project state to reflect the changes immediately
          console.log('15. Updating project state...');
          setProject(prev => ({
            ...prev,
            components: components
          }));
          console.log('16. Local state updated');
          
          // Clear emergency timeout since we succeeded
          console.log('17. Clearing emergency timeout...');
          if (emergencyTimeout) clearTimeout(emergencyTimeout);
          
          // Show success message with flamingo celebration
          console.log('18. Setting success message...');
          setFlamingoMessage(`🎉 Successfully uploaded ${uploadedPhotos.length} fabric photo${uploadedPhotos.length > 1 ? 's' : ''}!`);
          
          console.log('19. Setting 1.5s timeout to hide loader...');
          setTimeout(() => {
            console.log('20. SUCCESS TIMEOUT: Hiding flamingo loader after 1.5s');
            setShowFlamingoLoader(false);
          }, 1500); // Show success for 1.5 seconds
          
          // Failsafe timeout in case something goes wrong
          console.log('21. Setting 3s failsafe timeout...');
          setTimeout(() => {
            console.log('22. FAILSAFE TIMEOUT: Hiding flamingo loader after 3s');
            setShowFlamingoLoader(false);
          }, 3000);
        } else {
          throw new Error(`Component with ID ${componentId} not found in project`);
        }
      } else {
        throw new Error('Project not found in database');
      }
      console.log('=== FABRIC UPLOAD DEBUG END (SUCCESS) ===');
    } catch (error) {
      console.error('=== FABRIC UPLOAD ERROR ===');
      console.error('Error object:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
      if (emergencyTimeout) {
        console.log('Clearing emergency timeout due to error...');
        clearTimeout(emergencyTimeout);
      }
      
      setShowFlamingoLoader(false);
      alert('Failed to upload fabric photos: ' + error.message);
      console.log('=== FABRIC UPLOAD DEBUG END (ERROR) ===');
    } finally {
      console.log('FINALLY: Resetting upload state');
      setUploadingFabricPhotos(prev => ({ ...prev, [componentId]: false }));
    }
  };

  // Handle component photo upload
  const handleComponentPhotoUpload = async (files, componentId) => {
    // Emergency timeout to prevent infinite loading
    let emergencyTimeout;
    
    try {
      setUploadingComponentPhotos(prev => ({ ...prev, [componentId]: true }));
      setFlamingoKey(Date.now()); // Unique key for new dance
      setFlamingoMessage(`Uploading ${files.length} beautiful component photo${files.length > 1 ? 's' : ''}...`);
      setShowFlamingoLoader(true);
      
      emergencyTimeout = setTimeout(() => {
        console.error('Emergency timeout: Hiding flamingo loader after 60 seconds');
        setShowFlamingoLoader(false);
        setUploadingComponentPhotos(prev => ({ ...prev, [componentId]: false }));
      }, 60000);
      
      const uploadedPhotos = await uploadComponentPhotos(files, componentId);
      
      // Update the component's photos in Firestore
      const projectRef = doc(db, `projects/${id}`);
      const projectDoc = await getDoc(projectRef);
      
      if (projectDoc.exists()) {
        const projectData = projectDoc.data();
        const components = projectData.components || [];
        const componentIndex = components.findIndex(c => String(c.id) === String(componentId));
        
        if (componentIndex !== -1) {
          const component = components[componentIndex];
          const currentComponentPhotos = component.taxonomyData?.componentPhotos || { imageUrls: [], imageCount: 0 };
          
          const updatedComponentPhotos = {
            imageUrls: [...(currentComponentPhotos.imageUrls || []), ...uploadedPhotos.map(photo => photo.url)],
            imageCount: (currentComponentPhotos.imageCount || 0) + uploadedPhotos.length
          };
          
          components[componentIndex] = {
            ...component,
            taxonomyData: {
              ...component.taxonomyData,
              componentPhotos: updatedComponentPhotos
            }
          };
          
          await updateDoc(projectRef, {
            components: components,
            updatedAt: serverTimestamp()
          });
          
          // Update the local state instead of reloading the entire project
          console.log('Updating local component photos state...');
          setComponentPhotos(prev => ({
            ...prev,
            [componentId]: updatedComponentPhotos.imageUrls
          }));
          
          // Also update the project state
          setProject(prev => ({
            ...prev,
            components: components
          }));
          
          // Clear emergency timeout since we succeeded
          if (emergencyTimeout) clearTimeout(emergencyTimeout);
          
          // Show success message with flamingo celebration
          setFlamingoMessage(`🎉 Successfully uploaded ${uploadedPhotos.length} component photo${uploadedPhotos.length > 1 ? 's' : ''}!`);
        setTimeout(() => {
          console.log('Hiding flamingo loader');
          setShowFlamingoLoader(false);
        }, 1500); // Show success for 1.5 seconds
        
          // Failsafe timeout in case something goes wrong
          setTimeout(() => {
            console.log('Failsafe: Hiding flamingo loader');
            setShowFlamingoLoader(false);
          }, 3000);
        } else {
          throw new Error(`Component with ID ${componentId} not found in project`);
        }
      } else {
        throw new Error('Project not found in database');
      }
    } catch (error) {
      if (emergencyTimeout) clearTimeout(emergencyTimeout);
      console.error('Error uploading component photos:', error);
      setShowFlamingoLoader(false);
      alert('Failed to upload component photos: ' + error.message);
    } finally {
      setUploadingComponentPhotos(prev => ({ ...prev, [componentId]: false }));
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e, componentId, type) => {
    e.preventDefault();
    setDragOverStates(prev => ({ ...prev, [`${componentId}-${type}`]: true }));
  };

  const handleDragLeave = (e, componentId, type) => {
    e.preventDefault();
    setDragOverStates(prev => ({ ...prev, [`${componentId}-${type}`]: false }));
  };

  const handleDrop = (e, componentId, type) => {
    e.preventDefault();
    setDragOverStates(prev => ({ ...prev, [`${componentId}-${type}`]: false }));
    
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length > 0) {
      if (type === 'fabric') {
        handleFabricPhotoUpload(imageFiles, componentId);
      } else if (type === 'component') {
        handleComponentPhotoUpload(imageFiles, componentId);
      }
    }
  };

  // Delete photo functions
  const deleteFabricPhoto = async (componentId, photoUrl, photoIndex) => {
    console.log('=== FABRIC DELETE DEBUG START ===');
    console.log('Component ID:', componentId);
    console.log('Photo URL:', photoUrl);
    console.log('Photo Index:', photoIndex);
    
    if (!window.confirm('Are you sure you want to delete this fabric photo?')) {
      console.log('User cancelled delete');
      return;
    }
    
    // Emergency timeout to prevent infinite loading
    let emergencyTimeout;
    
    try {
      console.log('1. Setting delete state...');
      setFlamingoKey(Date.now());
      setFlamingoMessage('Removing fabric photo...');
      setShowFlamingoLoader(true);
      
      console.log('2. Setting emergency timeout (30s)...');
      emergencyTimeout = setTimeout(() => {
        console.error('DELETE EMERGENCY TIMEOUT: Hiding flamingo loader after 30 seconds');
        setShowFlamingoLoader(false);
      }, 30000);

      console.log('3. Getting project document from Firestore...');
      const projectRef = doc(db, `projects/${id}`);
      const projectDoc = await getDoc(projectRef);
      console.log('4. Project doc exists?', projectDoc.exists());
      
      if (projectDoc.exists()) {
        console.log('5. Project found, getting components...');
        const projectData = projectDoc.data();
        const components = projectData.components || [];
        console.log('5a. Total components:', components.length);
        
        console.log('6. Finding component with ID:', componentId);
        const componentIndex = components.findIndex(c => String(c.id) === String(componentId));
        console.log('6a. Component found at index:', componentIndex);
        
        if (componentIndex !== -1) {
          console.log('7. Component found, extracting current photos...');
          const component = components[componentIndex];
          console.log('7a. Current component:', component);
          const currentFabricPhotos = component.taxonomyData?.fabricPhotos || { imageUrls: [], imageCount: 0 };
          console.log('7b. Current fabric photos:', currentFabricPhotos);
          
          console.log('8. Removing photo from array...');
          const updatedUrls = currentFabricPhotos.imageUrls.filter((_, index) => index !== photoIndex);
          console.log('8a. Updated URLs array:', updatedUrls);
          
          const updatedFabricPhotos = {
            imageUrls: updatedUrls,
            imageCount: updatedUrls.length
          };
          console.log('8b. Updated fabric photos object:', updatedFabricPhotos);
          
          console.log('9. Updating component in array...');
          components[componentIndex] = {
            ...component,
            taxonomyData: {
              ...component.taxonomyData,
              fabricPhotos: updatedFabricPhotos
            }
          };
          
          console.log('10. Updating project document...');
          await updateDoc(projectRef, {
            components: components,
            updatedAt: serverTimestamp()
          });
          console.log('11. Firestore update complete');
        
          // Delete from Firebase Storage (optional - storage cleanup)
          console.log('12. Attempting to delete from Firebase Storage...');
          try {
            // Extract path from Firebase Storage URL
            const urlParts = photoUrl.split('/o/')[1]?.split('?')[0];
            console.log('12a. URL parts:', urlParts);
            if (urlParts) {
              const storagePath = decodeURIComponent(urlParts);
              console.log('12b. Storage path:', storagePath);
              const photoRef = ref(storage, storagePath);
              await deleteObject(photoRef);
              console.log('12c. Successfully deleted from storage');
            } else {
              console.warn('12d. Could not parse storage path from URL:', photoUrl);
            }
          } catch (storageError) {
            console.warn('12e. Storage delete error (non-critical):', storageError);
          }
          
          console.log('13. Updating local state...');
          setFabricPhotos(prev => ({
            ...prev,
            [componentId]: updatedUrls
          }));
          
          // Also update the project state
          console.log('14. Updating project state...');
          setProject(prev => ({
            ...prev,
            components: components
          }));
        
          console.log('15. Clearing emergency timeout...');
          if (emergencyTimeout) clearTimeout(emergencyTimeout);
          
          console.log('16. Setting success message...');
          setFlamingoMessage('🗑️ Fabric photo removed successfully!');
          
          console.log('17. Setting 1s timeout to hide loader...');
          setTimeout(() => {
            console.log('18. SUCCESS: Hiding loader after 1s');
            setShowFlamingoLoader(false);
          }, 1000);
          
          console.log('19. Setting 2.5s failsafe timeout...');
          setTimeout(() => {
            console.log('20. FAILSAFE: Hiding loader after 2.5s');
            setShowFlamingoLoader(false);
          }, 2500);
        } else {
          throw new Error(`Component with ID ${componentId} not found in project`);
        }
      } else {
        console.error('ERROR: Component document does not exist!');
        throw new Error('Component not found in database');
      }
      console.log('=== FABRIC DELETE DEBUG END (SUCCESS) ===');
    } catch (error) {
      console.error('=== FABRIC DELETE ERROR ===');
      console.error('Error object:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
      if (emergencyTimeout) {
        console.log('Clearing emergency timeout due to error...');
        clearTimeout(emergencyTimeout);
      }
      setShowFlamingoLoader(false);
      alert('Failed to delete fabric photo: ' + error.message);
      console.log('=== FABRIC DELETE DEBUG END (ERROR) ===');
    }
  };

  const deleteComponentPhoto = async (componentId, photoUrl, photoIndex) => {
    if (!window.confirm('Are you sure you want to delete this component photo?')) return;
    
    // Emergency timeout to prevent infinite loading
    let emergencyTimeout;
    
    try {
      setFlamingoKey(Date.now());
      setFlamingoMessage('Removing component photo...');
      setShowFlamingoLoader(true);
      
      emergencyTimeout = setTimeout(() => {
        console.error('Emergency timeout: Hiding flamingo loader after 30 seconds for delete');
        setShowFlamingoLoader(false);
      }, 30000);

      // Get current project data
      const projectRef = doc(db, `projects/${id}`);
      const projectDoc = await getDoc(projectRef);
      
      if (projectDoc.exists()) {
        const projectData = projectDoc.data();
        const components = projectData.components || [];
        const componentIndex = components.findIndex(c => String(c.id) === String(componentId));
        
        if (componentIndex !== -1) {
          const component = components[componentIndex];
          const currentComponentPhotos = component.taxonomyData?.componentPhotos || { imageUrls: [], imageCount: 0 };
          
          // Remove the photo URL from the array
          const updatedUrls = currentComponentPhotos.imageUrls.filter((_, index) => index !== photoIndex);
          
          const updatedComponentPhotos = {
            imageUrls: updatedUrls,
            imageCount: updatedUrls.length
          };
          
          components[componentIndex] = {
            ...component,
            taxonomyData: {
              ...component.taxonomyData,
              componentPhotos: updatedComponentPhotos
            }
          };
          
          await updateDoc(projectRef, {
            components: components,
            updatedAt: serverTimestamp()
          });
        
        // Delete from Firebase Storage (optional - storage cleanup)
        try {
          // Extract path from Firebase Storage URL
          const urlParts = photoUrl.split('/o/')[1]?.split('?')[0];
          if (urlParts) {
            const storagePath = decodeURIComponent(urlParts);
            const photoRef = ref(storage, storagePath);
            await deleteObject(photoRef);
            console.log('Successfully deleted component photo from storage:', storagePath);
          } else {
            console.warn('Could not parse storage path from URL:', photoUrl);
          }
        } catch (storageError) {
          console.warn('Could not delete component photo from storage:', storageError);
        }
        
          // Update local state instead of reloading entire project  
          setComponentPhotos(prev => ({
            ...prev,
            [componentId]: updatedUrls
          }));
          
          // Also update the project state
          setProject(prev => ({
            ...prev,
            components: components
          }));
          
          // Clear emergency timeout since we succeeded
          if (emergencyTimeout) clearTimeout(emergencyTimeout);
          
          setFlamingoMessage('🗑️ Component photo removed successfully!');
          setTimeout(() => {
            setShowFlamingoLoader(false);
          }, 1000);
          
          // Failsafe timeout
          setTimeout(() => {
            setShowFlamingoLoader(false);
          }, 2500);
        } else {
          throw new Error(`Component with ID ${componentId} not found in project`);
        }
      } else {
        throw new Error('Project not found in database');
      }
    } catch (error) {
      if (emergencyTimeout) clearTimeout(emergencyTimeout);
      console.error('Error deleting component photo:', error);
      setShowFlamingoLoader(false);
      alert('Failed to delete component photo: ' + error.message);
    }
  };

  const handleMilestoneUpdate = (updatedMilestones) => {
    console.log('Milestone update received:', updatedMilestones);
    setMilestones(updatedMilestones);
    // Don't reload immediately - the MilestoneManager already saved to Firestore
    setTimeout(() => {
      loadProject(); // Reload after a delay to get updated data from Firestore
    }, 1000);
  };

  const loadProjectPhotos = async () => {
    try {
      // Load general project photos
      const photosQuery = query(
        collection(db, 'projectPhotos'),
        where('projectId', '==', id)
      );
      
      const photosSnapshot = await getDocs(photosQuery);
      let photosData = photosSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort manually by uploadedAt in descending order
      photosData = photosData.sort((a, b) => {
        const aDate = a.uploadedAt?.toDate ? a.uploadedAt.toDate() : new Date(a.uploadedAt);
        const bDate = b.uploadedAt?.toDate ? b.uploadedAt.toDate() : new Date(b.uploadedAt);
        return bDate - aDate;
      });
      
      console.log('Loaded general photos:', photosData);
      setPhotos(photosData);
      
      // Load component photos
      const componentPhotosQuery = query(
        collection(db, 'componentPhotos'),
        where('projectId', '==', id)
      );
      
      const componentPhotosSnapshot = await getDocs(componentPhotosQuery);
      let componentPhotosData = componentPhotosSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by uploadedAt in descending order
      componentPhotosData = componentPhotosData.sort((a, b) => {
        const aDate = a.uploadedAt?.toDate ? a.uploadedAt.toDate() : new Date(a.uploadedAt);
        const bDate = b.uploadedAt?.toDate ? b.uploadedAt.toDate() : new Date(b.uploadedAt);
        return bDate - aDate;
      });
      
      // Group component photos by componentId
      const groupedComponentPhotos = {};
      componentPhotosData.forEach(photo => {
        const componentId = photo.componentId;
        if (!groupedComponentPhotos[componentId]) {
          groupedComponentPhotos[componentId] = [];
        }
        groupedComponentPhotos[componentId].push(photo);
      });
      
      console.log('Loaded component photos:', groupedComponentPhotos);
      console.log('Component photos count by componentId:');
      Object.keys(groupedComponentPhotos).forEach(componentId => {
        console.log(`  ${componentId}: ${groupedComponentPhotos[componentId].length} photos`);
      });
      setComponentPhotos(groupedComponentPhotos);

      // Load fabric photos
      const fabricPhotosQuery = query(
        collection(db, 'fabricPhotos'),
        where('projectId', '==', id)
      );
      
      const fabricPhotosSnapshot = await getDocs(fabricPhotosQuery);
      let fabricPhotosData = fabricPhotosSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by uploadedAt in descending order
      fabricPhotosData = fabricPhotosData.sort((a, b) => {
        const aDate = a.uploadedAt?.toDate ? a.uploadedAt.toDate() : new Date(a.uploadedAt);
        const bDate = b.uploadedAt?.toDate ? b.uploadedAt.toDate() : new Date(b.uploadedAt);
        return bDate - aDate;
      });
      
      // Group fabric photos by componentId
      const groupedFabricPhotos = {};
      fabricPhotosData.forEach(photo => {
        const componentId = photo.componentId;
        if (!groupedFabricPhotos[componentId]) {
          groupedFabricPhotos[componentId] = [];
        }
        groupedFabricPhotos[componentId].push(photo);
      });
      
      console.log('Loaded fabric photos:', groupedFabricPhotos);
      setFabricPhotos(groupedFabricPhotos);
    } catch (error) {
      console.error('Error loading photos:', error);
      // Don't show error to user for photos - just log it
    }
  };

  const loadProjectTasks = async () => {
    try {
      const tasksQuery = query(
        collection(db, 'projects', id, 'tasks'),
        orderBy('createdAt', 'desc')
      );
      
      const tasksSnapshot = await getDocs(tasksQuery);
      const tasksData = tasksSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setTasks(tasksData);
    } catch (error) {
      console.error('Error loading tasks:', error);
      // Don't show error to user for tasks - just log it
    }
  };

  const startEditing = () => {
    const projectToEdit = { ...project };
    
    // Format dates for input fields
    if (projectToEdit.startDate) {
      const startDate = projectToEdit.startDate.seconds ? 
        new Date(projectToEdit.startDate.seconds * 1000) : 
        new Date(projectToEdit.startDate);
      projectToEdit.startDate = startDate.toISOString().split('T')[0];
    }
    
    if (projectToEdit.expectedEndDate) {
      const endDate = projectToEdit.expectedEndDate.seconds ? 
        new Date(projectToEdit.expectedEndDate.seconds * 1000) : 
        new Date(projectToEdit.expectedEndDate);
      projectToEdit.expectedEndDate = endDate.toISOString().split('T')[0];
    }
    
    setEditedProject(projectToEdit);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setEditedProject(null);
    setIsEditing(false);
  };

  const saveProject = async () => {
    try {
      setSaving(true);
      
      const projectRef = doc(db, `projects/${id}`);
      
      // Prepare update data with proper date conversion
      const updateData = {
        name: editedProject.name,
        briefDescription: editedProject.briefDescription,
        description: editedProject.description,
        projectDescription: editedProject.projectDescription,
        status: editedProject.status,
        updatedAt: new Date()
      };
      
      // Convert date strings back to Date objects
      if (editedProject.startDate) {
        updateData.startDate = new Date(editedProject.startDate);
      }
      if (editedProject.expectedEndDate) {
        updateData.expectedEndDate = new Date(editedProject.expectedEndDate);
      }
      
      await updateDoc(projectRef, updateData);

      setProject(editedProject);
      setIsEditing(false);
      setEditedProject(null);
      
      alert('Project updated successfully!');
    } catch (error) {
      console.error('Error updating project:', error);
      alert('Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProject = async () => {
    try {
      // Check if project has tasks
      const tasksSnapshot = await getDocs(collection(db, 'projects', id, 'tasks'));
      if (!tasksSnapshot.empty) {
        alert('⚠️ Cannot delete project!\n\nThis project has ' + tasksSnapshot.size + ' task(s).\n\nPlease delete all tasks before deleting the project.');
        return;
      }

      // Check if project has photos
      const photosQuery = query(
        collection(db, 'projectPhotos'),
        where('projectId', '==', id)
      );
      const photosSnapshot = await getDocs(photosQuery);
      if (!photosSnapshot.empty) {
        alert('⚠️ Cannot delete project!\n\nThis project has ' + photosSnapshot.size + ' photo(s).\n\nPlease delete all photos before deleting the project.');
        return;
      }

      // Note: Timeline events will be orphaned but that's okay for now
      // since they don't have deletion functionality yet

      // Double confirmation for project deletion
      const projectName = formatProjectName(project);
      const firstConfirm = window.confirm(
        `⚠️ DELETE PROJECT WARNING\n\nAre you absolutely sure you want to delete the project:\n"${projectName}"?\n\nThis action cannot be undone.`
      );
      
      if (!firstConfirm) return;

      // Second confirmation with type verification
      const userInput = window.prompt(
        `⚠️ FINAL CONFIRMATION\n\nTo confirm deletion of "${projectName}", please type:\n\nDelete Project\n\nType it exactly as shown:`
      );

      if (userInput !== 'Delete Project') {
        alert('Confirmation text does not match. Deletion cancelled.');
        return;
      }

      // Delete the project
      await deleteDoc(doc(db, `projects/${id}`));
      
      alert('✅ Project deleted successfully.');
      
      // Redirect to projects page
      navigate('/projects');
      
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project: ' + error.message);
    }
  };

  const handleDeletePhoto = async (photo) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }
    
    try {
      // Delete from Firestore
      await deleteDoc(doc(db, `projectPhotos/${photo.id}`));
      
      // Delete from Storage
      if (photo.filename) {
        const storagePath = `projects/${id}/photos/${photo.filename}`;
        const storageRef = ref(storage, storagePath);
        await deleteObject(storageRef);
      }
      
      // Reload photos
      alert('Photo deleted successfully!');
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Failed to delete photo');
    }
  };

  const handleDeleteComponentPhoto = async (photo) => {
    if (!window.confirm('Are you sure you want to delete this component photo?')) {
      return;
    }
    
    try {
      // Delete from Firestore
      await deleteDoc(doc(db, `componentPhotos/${photo.id}`));
      
      // Delete from Storage
      if (photo.path) {
        const storageRef = ref(storage, photo.path);
        await deleteObject(storageRef);
      }
      
      // Reload photos
      alert('Component photo deleted successfully!');
    } catch (error) {
      console.error('Error deleting component photo:', error);
      alert('Failed to delete component photo');
    }
  };

  const handleDeleteFabricPhoto = async (photo) => {
    if (!window.confirm('Are you sure you want to delete this fabric photo?')) {
      return;
    }
    
    try {
      // Delete from Firestore
      await deleteDoc(doc(db, `fabricPhotos/${photo.id}`));
      
      // Delete from Storage
      if (photo.path) {
        const storageRef = ref(storage, photo.path);
        await deleteObject(storageRef);
      }
      
      // Reload photos
      alert('Fabric photo deleted successfully!');
    } catch (error) {
      console.error('Error deleting fabric photo:', error);
      alert('Failed to delete fabric photo');
    }
  };

  const handleDeleteComponent = async (componentId) => {
    if (!window.confirm('Are you sure you want to delete this component? This action cannot be undone.')) {
      return;
    }
    
    try {
      setSaving(true);
      
      // Remove component from project's components array
      const updatedComponents = project.components.filter(c => c.id !== componentId);
      const updatedProject = { ...project, components: updatedComponents };
      
      // Update project in Firestore
      await updateDoc(doc(db, `projects/${id}`), {
        components: updatedComponents,
        updatedAt: serverTimestamp()
      });
      
      // Update local state
      setProject(updatedProject);
      
    } catch (error) {
      console.error('Error deleting component:', error);
      alert('Failed to delete component. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleComponentWizardComplete = async (quoteData) => {
    try {
      setSaving(true);
      
      console.log('=== ProjectDetailPage Component Wizard Complete ===');
      console.log('Received quoteData:', quoteData);
      console.log('uploadedFabricUrls:', quoteData.uploadedFabricUrls);
      console.log('uploadedComponentUrls:', quoteData.uploadedComponentUrls);
      
      // Since images are already uploaded during the wizard, store the URLs
      const cleanFabric = quoteData.fabric ? {
        description: quoteData.fabric.description || '',
        imageUrls: quoteData.uploadedFabricUrls || [],
        imageCount: quoteData.uploadedFabricUrls ? quoteData.uploadedFabricUrls.length : 0
      } : null;

      const cleanComponentPhotos = quoteData.uploadedComponentUrls ? {
        imageUrls: quoteData.uploadedComponentUrls || [],
        imageCount: quoteData.uploadedComponentUrls.length
      } : null;
      
      console.log('cleanFabric:', cleanFabric);
      console.log('cleanComponentPhotos:', cleanComponentPhotos);

      let updatedComponents;
      let componentId;
      
      if (editingComponent) {
        // Edit existing component
        componentId = editingComponent.id;
        const updatedComponent = {
          ...editingComponent,
          name: quoteData.componentName || editingComponent.name,
          taxonomyData: {
            product: quoteData.product,
            category: quoteData.category,
            subcategory: quoteData.subcategory,
            features: quoteData.features || [],
            additionals: quoteData.additionals || [],
            dimensions: quoteData.dimensions || {},
            fabric: cleanFabric,
            componentPhotos: cleanComponentPhotos,
            materials: quoteData.materials || [],
            description: quoteData.description || '',
            generatedDescription: quoteData.generatedDescription || ''
          }
        };
        
        updatedComponents = project.components.map(comp => 
          comp.id === editingComponent.id ? updatedComponent : comp
        );
      } else {
        // Add new component
        componentId = Date.now();
        const newComponent = {
          id: componentId,
          name: quoteData.componentName || `Component ${(project.components?.length || 0) + 1}`,
          taxonomyData: {
            product: quoteData.product,
            category: quoteData.category,
            subcategory: quoteData.subcategory,
            features: quoteData.features || [],
            additionals: quoteData.additionals || [],
            dimensions: quoteData.dimensions || {},
            fabric: cleanFabric,
            componentPhotos: cleanComponentPhotos,
            materials: quoteData.materials || [],
            description: quoteData.description || '',
            generatedDescription: quoteData.generatedDescription || ''
          }
        };

        updatedComponents = [...(project.components || []), newComponent];
      }

      const updatedProject = { ...project, components: updatedComponents };

      // Update Firestore
      await updateDoc(doc(db, `projects/${id}`), {
        components: updatedComponents,
        updatedAt: serverTimestamp()
      });

      // Update local state
      setProject(updatedProject);
      setShowComponentWizard(false);
      setEditingComponent(null);
      setCurrentComponentId(null);
      
      // Reload project data to fetch newly uploaded images
      await loadProject();

      // Images were already uploaded during the wizard, no additional upload needed

      alert(editingComponent ? 'Component updated successfully!' : 'Component added successfully!');
      
    } catch (error) {
      console.error('Error adding component:', error);
      alert('Failed to add component');
    } finally {
      setSaving(false);
    }
  };

  const handleEditComponent = (component) => {
    setEditingComponent(component);
    setCurrentComponentId(component.id);
    setShowComponentWizard(true);
  };

  const handleComponentWizardCancel = () => {
    setShowComponentWizard(false);
    setEditingComponent(null);
    setCurrentComponentId(null);
    setNewComponentName('');
  };

  const handleNameModalConfirm = () => {
    if (newComponentName.trim()) {
      setShowNameModal(false);
      setCurrentComponentId(Date.now()); // Generate new component ID
      setShowComponentWizard(true);
    }
  };

  const handleNameModalCancel = () => {
    setShowNameModal(false);
    setNewComponentName('');
  };

  const handleEditChange = (field, value) => {
    setEditedProject(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-gradient-to-r from-green-400 to-emerald-500 text-white';
      case 'in_progress':
        return 'bg-gradient-to-r from-blue-400 to-indigo-500 text-white';
      case 'planning':
        return 'bg-gradient-to-r from-orange-400 to-pink-500 text-white';
      default:
        return 'bg-gradient-to-r from-gray-400 to-gray-500 text-white';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };


  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading project...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (error || !project) {
    return (
      <AppLayout>
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Project Not Found</h3>
          <p className="text-gray-500 mb-6">{error || 'The requested project could not be found.'}</p>
          <Link
            to="/projects"
            className="inline-flex items-center px-4 py-2 bg-pink-600 text-white text-sm font-medium rounded-md hover:bg-pink-700 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Projects
          </Link>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="relative">
        {/* Header */}
        <BrandGradient className="rounded-2xl p-8 mb-8 text-white shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <Link
              to="/projects"
              className="inline-flex items-center px-4 py-2 bg-black bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-all duration-300 backdrop-blur-sm border border-white border-opacity-30"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to Projects
            </Link>
            <div className="flex items-center space-x-4">
              <span className={`px-4 py-2 rounded-full font-semibold shadow-lg ${getStatusColor(project.status)}`}>
                {project.status?.replace('_', ' ') || 'planning'}
              </span>
              {!isEditing ? (
                <>
                  <button
                    onClick={startEditing}
                    className="inline-flex items-center px-4 py-2 bg-black bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-all duration-300 backdrop-blur-sm border border-white border-opacity-30"
                  >
                    <PencilIcon className="w-4 h-4 mr-2" />
                    Edit Project
                  </button>
                  <button
                    onClick={handleDeleteProject}
                    className="inline-flex items-center px-4 py-2 bg-red-600 bg-opacity-20 text-red-100 rounded-lg hover:bg-opacity-30 transition-all duration-300 backdrop-blur-sm border border-red-400 border-opacity-30"
                    title="Delete this project (requires all tasks and content to be deleted first)"
                  >
                    <TrashIcon className="w-4 h-4 mr-2" />
                    Delete Project
                  </button>
                </>
              ) : (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={saveProject}
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    <CheckCircleIcon className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={cancelEditing}
                    className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4 mr-2" />
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-start">
            <PFISymbol size="lg" className="mr-6 text-white filter drop-shadow-lg" />
            <div className="flex-1">
              {isEditing ? (
                <div className="space-y-4 mb-4">
                  <input
                    type="text"
                    value={editedProject?.name || ''}
                    onChange={(e) => handleEditChange('name', e.target.value)}
                    className="w-full text-4xl font-bold bg-black bg-opacity-20 text-white rounded-lg px-4 py-2 placeholder-pink-200 backdrop-blur-sm border border-white border-opacity-30"
                    placeholder="Project Name"
                  />
                  <input
                    type="text"
                    value={editedProject?.briefDescription || ''}
                    onChange={(e) => handleEditChange('briefDescription', e.target.value)}
                    className="w-full text-xl bg-black bg-opacity-20 text-white rounded-lg px-4 py-2 placeholder-pink-200 backdrop-blur-sm border border-white border-opacity-30"
                    placeholder="Brief Description (one sentence summary)"
                  />
                  <textarea
                    value={editedProject?.description || ''}
                    onChange={(e) => handleEditChange('description', e.target.value)}
                    className="w-full text-lg bg-black bg-opacity-20 text-white rounded-lg px-4 py-2 placeholder-pink-200 backdrop-blur-sm border border-white border-opacity-30"
                    rows="4"
                    placeholder="Full Description (structured product details)"
                  />
                  <textarea
                    value={editedProject?.projectDescription || ''}
                    onChange={(e) => handleEditChange('projectDescription', e.target.value)}
                    className="w-full text-lg bg-black bg-opacity-20 text-white rounded-lg px-4 py-2 placeholder-pink-200 backdrop-blur-sm border border-white border-opacity-30"
                    rows="4"
                    placeholder="Project Details (includes additional information)"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <select
                      value={editedProject?.status || 'planning'}
                      onChange={(e) => handleEditChange('status', e.target.value)}
                      className="w-full text-lg bg-black bg-opacity-20 text-white rounded-lg px-4 py-2 backdrop-blur-sm border border-white border-opacity-30"
                    >
                      <option value="planning" className="text-gray-900">Planning</option>
                      <option value="in_progress" className="text-gray-900">In Progress</option>
                      <option value="completed" className="text-gray-900">Completed</option>
                      <option value="on_hold" className="text-gray-900">On Hold</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-pink-100 text-sm font-medium mb-1">Start Date</label>
                      <input
                        type="date"
                        value={editedProject?.startDate || ''}
                        onChange={(e) => handleEditChange('startDate', e.target.value)}
                        className="w-full text-lg bg-black bg-opacity-20 text-white rounded-lg px-4 py-2 backdrop-blur-sm border border-white border-opacity-30"
                      />
                    </div>
                    <div>
                      <label className="block text-pink-100 text-sm font-medium mb-1">Expected End Date</label>
                      <input
                        type="date"
                        value={editedProject?.expectedEndDate || ''}
                        onChange={(e) => handleEditChange('expectedEndDate', e.target.value)}
                        className="w-full text-lg bg-black bg-opacity-20 text-white rounded-lg px-4 py-2 backdrop-blur-sm border border-white border-opacity-30"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <h1 className="text-4xl font-bold mb-3">{formatProjectName(project)}</h1>
                  {project.briefDescription && (
                    <p className="text-pink-100 text-xl mb-4">{project.briefDescription}</p>
                  )}
                </div>
              )}
              <div className="flex items-center space-x-6 text-pink-100">
                <div className="flex items-center">
                  <CalendarIcon className="w-5 h-5 mr-2" />
                  Created: {formatDate(project.createdAt)}
                </div>
              </div>
            </div>
          </div>
        </BrandGradient>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Project Information */}
          <div className="lg:col-span-2 space-y-8">
            {/* Full Description */}
            {project.description && (
              <div className="bg-white rounded-2xl shadow-xl border border-pink-100 p-8">
                <div className="flex items-center mb-6">
                  <ClipboardDocumentListIcon className="w-7 h-7 text-pink-600 mr-3" />
                  <h2 className="text-2xl font-bold text-gray-900">Full Description</h2>
                </div>
                <div className="prose max-w-none">
                  <p className="text-gray-700 text-lg leading-relaxed whitespace-pre-wrap">
                    {project.description}
                  </p>
                </div>
              </div>
            )}
            
            {/* Project Description with Additional Info */}
            {project.projectDescription && (
              <div className="bg-white rounded-2xl shadow-xl border border-pink-100 p-8">
                <div className="flex items-center mb-6">
                  <ClipboardDocumentListIcon className="w-7 h-7 text-pink-600 mr-3" />
                  <h2 className="text-2xl font-bold text-gray-900">Project Details</h2>
                </div>
                <div className="prose max-w-none">
                  <p className="text-gray-700 text-lg leading-relaxed whitespace-pre-wrap">
                    {project.projectDescription}
                  </p>
                </div>
              </div>
            )}

            {/* Project Timeline - Gantt Chart */}
            <div className="bg-white rounded-2xl shadow-xl border border-purple-100 p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <FlagIcon className="w-7 h-7 text-purple-600 mr-3" />
                  <h2 className="text-2xl font-bold text-gray-900">Project Timeline</h2>
                  <span className="ml-3 bg-purple-100 text-purple-800 text-sm px-3 py-1 rounded-full">
                    {milestones.length} Milestone{milestones.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <button
                  onClick={() => setShowMilestoneManager(true)}
                  className="inline-flex items-center px-4 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <FlagIcon className="w-4 h-4 mr-2" />
                  Manage Milestones
                </button>
              </div>
              
              <CustomerGanttChart 
                project={project}
                milestones={milestones}
                tasks={tasks}
                isAdmin={true}
                onMilestoneUpdate={() => setShowMilestoneManager(true)}
              />
            </div>

            {/* Project Components Details */}
            {project.components && project.components.length > 0 ? (
              <div className="bg-white rounded-2xl shadow-xl border border-green-100 p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <svg className="w-7 h-7 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <h2 className="text-2xl font-bold text-gray-900">Project Components</h2>
                    <span className="ml-3 bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full">
                      {project.components.length} Component{project.components.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <button
                    onClick={() => setShowNameModal(true)}
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <PlusIcon className="w-4 h-4 mr-2" />
                    Add Component
                  </button>
                </div>
                
                <div className="space-y-6">
                  {project.components.map((component, index) => {
                    const compPhotos = componentPhotos[component.id] || [];
                    const fabPhotos = fabricPhotos[component.id] || [];
                    return (
                      <div key={component.id} className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                            <span className="bg-green-600 text-white text-sm px-3 py-1 rounded-full mr-3">
                              {index + 1}
                            </span>
                            {component.name}
                          </h3>
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => handleEditComponent(component)}
                              className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit component"
                            >
                              <PencilIcon className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteComponent(component.id)}
                              className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete component"
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {/* Component Details */}
                          <div className="space-y-4">
                            {/* Show taxonomy data if available */}
                            {component.taxonomyData && (
                              <>
                                {component.taxonomyData.product && (
                                  <div>
                                    <h4 className="font-semibold text-gray-700 mb-1">Product Type</h4>
                                    <p className="text-gray-600">{component.taxonomyData.product.label}</p>
                                  </div>
                                )}
                                
                                {component.taxonomyData.category && (
                                  <div>
                                    <h4 className="font-semibold text-gray-700 mb-1">Category</h4>
                                    <p className="text-gray-600">{component.taxonomyData.category.label}</p>
                                  </div>
                                )}
                                
                                {component.taxonomyData.subcategory && (
                                  <div>
                                    <h4 className="font-semibold text-gray-700 mb-1">Style</h4>
                                    <p className="text-gray-600">{component.taxonomyData.subcategory.label}</p>
                                  </div>
                                )}
                                
                                {component.taxonomyData.features && component.taxonomyData.features.length > 0 && (
                                  <div>
                                    <h4 className="font-semibold text-gray-700 mb-1">Features</h4>
                                    <div className="flex flex-wrap gap-1">
                                      {component.taxonomyData.features.map((feature, idx) => (
                                        <span key={idx} className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full">
                                          {feature.label || feature}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {component.taxonomyData.dimensions && Object.keys(component.taxonomyData.dimensions).length > 0 && (
                                  <div>
                                    <h4 className="font-semibold text-gray-700 mb-1">Dimensions</h4>
                                    <div className="grid grid-cols-2 gap-2">
                                      {Object.entries(component.taxonomyData.dimensions).map(([key, value]) => (
                                        <div key={key} className="bg-white p-2 rounded border">
                                          <span className="text-xs text-gray-500 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}: </span>
                                          <span className="font-medium text-gray-800">{value}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Fabric Photos Section with Drag & Drop */}
                                {(() => {
                                  const allFabricPhotos = fabricPhotos[component.id] || [];
                                  
                                  if (allFabricPhotos.length > 0) {
                                    return (
                                      <div>
                                    <h4 className="font-semibold text-gray-700 mb-1">Fabric Photos</h4>
                                    <p className="text-xs text-gray-500 mb-2">
                                      {allFabricPhotos.length} fabric photo{allFabricPhotos.length !== 1 ? 's' : ''} uploaded
                                    </p>
                                    
                                    {/* Fabric Photos Grid with Delete Buttons */}
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                                      {allFabricPhotos.slice(0, 6).map((imageUrl, photoIndex) => (
                                          <div key={photoIndex} className="relative group">
                                            <img
                                              src={imageUrl}
                                              alt={`Fabric photo ${photoIndex + 1}`}
                                              className="w-full h-20 object-cover rounded-lg border border-gray-200 hover:scale-105 transition-transform cursor-pointer"
                                              onClick={() => window.open(imageUrl, '_blank')}
                                            />
                                            <div className="absolute top-1 right-1 bg-black bg-opacity-50 text-white rounded-full p-1 text-xs">
                                              {photoIndex + 1}
                                            </div>
                                            {/* Delete Button */}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                deleteFabricPhoto(component.id, imageUrl, photoIndex);
                                              }}
                                              className="absolute top-1 left-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                              title="Delete photo"
                                            >
                                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                              </svg>
                                            </button>
                                          </div>
                                        ))}
                                        {allFabricPhotos.length > 6 && (
                                          <div className="w-full h-20 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 text-sm">
                                            +{allFabricPhotos.length - 6} more
                                          </div>
                                        )}
                                      </div>
                                    
                                    {/* Drag & Drop Zone for More Photos */}
                                    <div
                                      onDragOver={(e) => handleDragOver(e, component.id, 'fabric')}
                                      onDragLeave={(e) => handleDragLeave(e, component.id, 'fabric')}
                                      onDrop={(e) => handleDrop(e, component.id, 'fabric')}
                                      className={`mt-2 border-2 border-dashed rounded-lg p-3 text-center transition-colors ${
                                        dragOverStates[`${component.id}-fabric`]
                                          ? 'border-pink-400 bg-pink-50'
                                          : 'border-gray-300 hover:border-pink-300'
                                      }`}
                                    >
                                      <p className="text-xs text-gray-500">Drop more fabric photos here or</p>
                                      <button
                                        onClick={() => document.getElementById(`fabric-upload-${component.id}`)?.click()}
                                        disabled={uploadingFabricPhotos[component.id]}
                                        className={`text-xs font-medium transition-colors ${
                                          uploadingFabricPhotos[component.id]
                                            ? 'text-gray-400 cursor-not-allowed'
                                            : 'text-pink-600 hover:text-pink-700'
                                        }`}
                                      >
                                        {uploadingFabricPhotos[component.id] ? 'Uploading...' : 'click to add more'}
                                      </button>
                                    </div>

                                    <input
                                      id={`fabric-upload-${component.id}`}
                                      type="file"
                                      multiple
                                      accept="image/*"
                                      onChange={(e) => {
                                        const files = Array.from(e.target.files);
                                        if (files.length > 0) {
                                          handleFabricPhotoUpload(files, component.id);
                                        }
                                        e.target.value = '';
                                      }}
                                      className="hidden"
                                    />
                                  </div>
                                    );
                                  } else {
                                    // Show upload area when no photos exist
                                    return (
                                      <div>
                                        <h4 className="font-semibold text-gray-700 mb-1">Fabric Photos</h4>
                                        <div
                                          onDragOver={(e) => handleDragOver(e, component.id, 'fabric')}
                                          onDragLeave={(e) => handleDragLeave(e, component.id, 'fabric')}
                                          onDrop={(e) => handleDrop(e, component.id, 'fabric')}
                                          className={`border-2 border-dashed rounded-lg p-3 text-center transition-colors ${
                                            dragOverStates[`${component.id}-fabric`]
                                              ? 'border-pink-400 bg-pink-50'
                                              : 'border-gray-300 hover:border-pink-300'
                                          }`}
                                        >
                                          <svg className="mx-auto h-8 w-8 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                                          </svg>
                                          <div className="mt-1">
                                            <p className="text-xs text-gray-500">Drop fabric photos here or</p>
                                            <button
                                              onClick={() => document.getElementById(`fabric-upload-${component.id}`)?.click()}
                                              className="text-xs font-medium text-pink-600 hover:text-pink-700"
                                            >
                                              click to browse
                                            </button>
                                          </div>
                                        </div>
                                        
                                        <input
                                          id={`fabric-upload-${component.id}`}
                                          type="file"
                                          multiple
                                          accept="image/*"
                                          onChange={(e) => {
                                            const files = Array.from(e.target.files);
                                            if (files.length > 0) {
                                              handleFabricPhotoUpload(files, component.id);
                                            }
                                            e.target.value = '';
                                          }}
                                          className="hidden"
                                        />
                                      </div>
                                    );
                                  }
                                })()}
                                
                                {/* Component Photos Section with Drag & Drop */}
                                {component.taxonomyData.componentPhotos && component.taxonomyData.componentPhotos.imageCount > 0 ? (
                                  <div>
                                    <h4 className="font-semibold text-gray-700 mb-1">Component Photos</h4>
                                    <p className="text-xs text-gray-500 mb-2">
                                      {component.taxonomyData.componentPhotos.imageCount} component photo{component.taxonomyData.componentPhotos.imageCount !== 1 ? 's' : ''} uploaded
                                    </p>
                                    
                                    {/* Component Photos Grid with Delete Buttons */}
                                    {component.taxonomyData.componentPhotos.imageUrls && component.taxonomyData.componentPhotos.imageUrls.length > 0 && (
                                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                                        {component.taxonomyData.componentPhotos.imageUrls.slice(0, 6).map((imageUrl, photoIndex) => (
                                          <div key={photoIndex} className="relative group">
                                            <img
                                              src={imageUrl}
                                              alt={`Component photo ${photoIndex + 1}`}
                                              className="w-full h-20 object-cover rounded-lg border border-gray-200 hover:scale-105 transition-transform cursor-pointer"
                                              onClick={() => window.open(imageUrl, '_blank')}
                                            />
                                            <div className="absolute top-1 right-1 bg-black bg-opacity-50 text-white rounded-full p-1 text-xs">
                                              {photoIndex + 1}
                                            </div>
                                            {/* Delete Button */}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                deleteComponentPhoto(component.id, imageUrl, photoIndex);
                                              }}
                                              className="absolute top-1 left-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                              title="Delete photo"
                                            >
                                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                              </svg>
                                            </button>
                                          </div>
                                        ))}
                                        {component.taxonomyData.componentPhotos.imageUrls.length > 6 && (
                                          <div className="w-full h-20 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 text-sm">
                                            +{component.taxonomyData.componentPhotos.imageUrls.length - 6} more
                                          </div>
                                        )}
                                      </div>
                                    )}
                                    
                                    {/* Drag & Drop Zone for More Photos */}
                                    <div
                                      onDragOver={(e) => handleDragOver(e, component.id, 'component')}
                                      onDragLeave={(e) => handleDragLeave(e, component.id, 'component')}
                                      onDrop={(e) => handleDrop(e, component.id, 'component')}
                                      className={`mt-2 border-2 border-dashed rounded-lg p-3 text-center transition-colors ${
                                        dragOverStates[`${component.id}-component`]
                                          ? 'border-pink-400 bg-pink-50'
                                          : 'border-gray-300 hover:border-pink-300'
                                      }`}
                                    >
                                      <p className="text-xs text-gray-500">Drop more component photos here or</p>
                                      <button
                                        onClick={() => document.getElementById(`component-upload-${component.id}`)?.click()}
                                        disabled={uploadingComponentPhotos[component.id]}
                                        className={`text-xs font-medium transition-colors ${
                                          uploadingComponentPhotos[component.id]
                                            ? 'text-gray-400 cursor-not-allowed'
                                            : 'text-pink-600 hover:text-pink-700'
                                        }`}
                                      >
                                        {uploadingComponentPhotos[component.id] ? 'Uploading...' : 'click to add more'}
                                      </button>
                                    </div>

                                    <input
                                      id={`component-upload-${component.id}`}
                                      type="file"
                                      multiple
                                      accept="image/*"
                                      onChange={(e) => {
                                        const files = Array.from(e.target.files);
                                        if (files.length > 0) {
                                          handleComponentPhotoUpload(files, component.id);
                                        }
                                        e.target.value = '';
                                      }}
                                      className="hidden"
                                    />
                                  </div>
                                ) : (
                                  <div>
                                    <h4 className="font-semibold text-gray-700 mb-1">Component Photos</h4>
                                    <div
                                      onDragOver={(e) => handleDragOver(e, component.id, 'component')}
                                      onDragLeave={(e) => handleDragLeave(e, component.id, 'component')}
                                      onDrop={(e) => handleDrop(e, component.id, 'component')}
                                      className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                                        dragOverStates[`${component.id}-component`]
                                          ? 'border-pink-400 bg-pink-50'
                                          : 'border-gray-300 hover:border-pink-300'
                                      }`}
                                    >
                                      <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                      <div className="mt-2">
                                        <p className="text-sm text-gray-600">Drop component photos here or</p>
                                        <button
                                          onClick={() => document.getElementById(`component-upload-${component.id}`)?.click()}
                                          className="text-pink-600 hover:text-pink-700 font-medium"
                                        >
                                          click to browse
                                        </button>
                                      </div>
                                    </div>
                                    
                                    <input
                                      id={`component-upload-${component.id}`}
                                      type="file"
                                      multiple
                                      accept="image/*"
                                      onChange={(e) => {
                                        const files = Array.from(e.target.files);
                                        if (files.length > 0) {
                                          handleComponentPhotoUpload(files, component.id);
                                        }
                                        e.target.value = '';
                                      }}
                                      className="hidden"
                                    />
                                  </div>
                                )}
                                
                                {component.taxonomyData.description && (
                                  <div>
                                    <h4 className="font-semibold text-gray-700 mb-1">Generated Description</h4>
                                    <p className="text-gray-600 leading-relaxed">{component.taxonomyData.description}</p>
                                  </div>
                                )}
                              </>
                            )}
                            
                            {/* Fallback to basic component data if no taxonomy */}
                            {!component.taxonomyData && component.description && (
                              <div>
                                <h4 className="font-semibold text-gray-700 mb-1">Description</h4>
                                <p className="text-gray-600 leading-relaxed">{component.description}</p>
                              </div>
                            )}
                            
                            {component.expectedCompletionDate && (
                              <div>
                                <h4 className="font-semibold text-gray-700 mb-1">Expected Completion</h4>
                                <p className="text-gray-600">
                                  {new Date(component.expectedCompletionDate).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </p>
                              </div>
                            )}
                          </div>
                          
                          {/* Component Summary */}
                          <div className="space-y-4">
                            <div className="bg-white rounded-lg p-4 border border-gray-200">
                              <h4 className="font-semibold text-gray-700 mb-3">Component Summary</h4>
                              
                              {component.taxonomyData ? (
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Specification Status:</span>
                                    <span className="text-green-700 font-medium">✓ Complete</span>
                                  </div>
                                  
                                  {component.taxonomyData.fabric && component.taxonomyData.fabric.imageCount > 0 && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Fabric Images:</span>
                                      <span className="text-gray-800 font-medium">{component.taxonomyData.fabric.imageCount} selected</span>
                                    </div>
                                  )}
                                  
                                  {component.taxonomyData.componentPhotos && component.taxonomyData.componentPhotos.imageCount > 0 && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Component Photos:</span>
                                      <span className="text-gray-800 font-medium">{component.taxonomyData.componentPhotos.imageCount} uploaded</span>
                                    </div>
                                  )}
                                  
                                  {Object.keys(component.taxonomyData.dimensions || {}).length > 0 && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Dimensions:</span>
                                      <span className="text-gray-800 font-medium">{Object.keys(component.taxonomyData.dimensions).length} measurements</span>
                                    </div>
                                  )}
                                  
                                  {component.taxonomyData.features && component.taxonomyData.features.length > 0 && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-600">Features:</span>
                                      <span className="text-gray-800 font-medium">{component.taxonomyData.features.length} selected</span>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="text-center py-4 text-gray-500">
                                  <p className="text-sm">Basic component - no detailed specifications</p>
                                </div>
                              )}
                            </div>
                            
                            <div className="text-xs text-gray-500 bg-blue-50 border border-blue-200 rounded p-3">
                              <p className="font-medium text-blue-800 mb-1">💡 Component Information</p>
                              <p>Component details and specifications are captured through the Smart Quote Wizard. General project photos can be uploaded in the photos section below.</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-xl border border-green-100 p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <svg className="w-7 h-7 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <h2 className="text-2xl font-bold text-gray-900">Project Components</h2>
                    <span className="ml-3 bg-gray-100 text-gray-600 text-sm px-3 py-1 rounded-full">
                      0 Components
                    </span>
                  </div>
                  <button
                    onClick={() => setShowNameModal(true)}
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <PlusIcon className="w-4 h-4 mr-2" />
                    Add Component
                  </button>
                </div>
                
                <div className="text-center py-12 bg-gray-50 rounded-xl">
                  <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No components yet</h3>
                  <p className="text-gray-500 mb-4">Add components to define the specific items needed for this project</p>
                  <button
                    onClick={() => setShowNameModal(true)}
                    className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <PlusIcon className="w-5 h-5 mr-2" />
                    Add Your First Component
                  </button>
                </div>
              </div>
            )}
            
            {/* Smart Quote Data - Product Specifications */}
            {project.smartQuoteData && (
              <div className="bg-white rounded-2xl shadow-xl border border-blue-100 p-8">
                <div className="flex items-center mb-6">
                  <ClipboardDocumentListIcon className="w-7 h-7 text-blue-600 mr-3" />
                  <h2 className="text-2xl font-bold text-gray-900">Product Specifications</h2>
                </div>
                <div className="space-y-4">
                  {project.smartQuoteData.product && (
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-1">Product</h3>
                      <p className="text-gray-600">{project.smartQuoteData.product.label}</p>
                    </div>
                  )}
                  {project.smartQuoteData.category && (
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-1">Category</h3>
                      <p className="text-gray-600">{project.smartQuoteData.category.label}</p>
                    </div>
                  )}
                  {project.smartQuoteData.subcategory && (
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-1">Type</h3>
                      <p className="text-gray-600">{project.smartQuoteData.subcategory.label}</p>
                    </div>
                  )}
                  {project.smartQuoteData.features && project.smartQuoteData.features.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-1">Features</h3>
                      <p className="text-gray-600">
                        {project.smartQuoteData.features.map(f => f.label).join(', ')}
                      </p>
                    </div>
                  )}
                  {project.smartQuoteData.dimensions && Object.keys(project.smartQuoteData.dimensions).length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-1">Dimensions</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {Object.entries(project.smartQuoteData.dimensions).map(([key, value]) => (
                          <div key={key} className="bg-gray-50 p-2 rounded">
                            <span className="text-sm text-gray-600 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}: </span>
                            <span className="font-medium text-gray-800">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {project.smartQuoteData.fabric && (
                    <div>
                      <h3 className="font-semibold text-gray-700 mb-1">Fabric</h3>
                      <p className="text-gray-600">{project.smartQuoteData.fabric.description}</p>
                      {project.smartQuoteData.fabric.imageCount > 0 && (
                        <p className="text-sm text-gray-500 mt-1">
                          {project.smartQuoteData.fabric.imageCount} fabric image(s) uploaded
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Project Photos */}
            <div className="bg-white rounded-2xl shadow-xl border border-purple-100 p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <PhotoIcon className="w-7 h-7 text-purple-600 mr-3" />
                  <h2 className="text-2xl font-bold text-gray-900">General Project Photos</h2>
                </div>
                <div className="text-sm text-gray-500">
                  {photos.length + Object.values(componentPhotos).reduce((acc, photos) => acc + photos.length, 0)} total photo{(photos.length + Object.values(componentPhotos).reduce((acc, photos) => acc + photos.length, 0)) !== 1 ? 's' : ''}
                </div>
              </div>
              
              <div className="space-y-8">
                {/* General Project Photos */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    General Project Photos ({photos.length})
                  </h3>
                  {photos.length > 0 ? (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {photos.map((photo) => (
                          <div key={photo.id} className="relative group">
                            <img
                              src={photo.url}
                              alt={photo.caption || 'General project photo'}
                              className="w-full h-32 object-cover rounded-lg shadow-md transition-transform hover:scale-105 cursor-pointer"
                              onClick={() => setSelectedPhoto(photo)}
                            />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePhoto(photo);
                              }}
                              className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 z-10 shadow-lg"
                              title="Delete photo"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center pointer-events-none">
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                <div className="bg-white bg-opacity-90 rounded-full p-2">
                                  <PhotoIcon className="w-6 h-6 text-gray-700" />
                                </div>
                              </div>
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2 rounded-b-lg pointer-events-none">
                              <div className="text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                                {photo.caption || 'Click to view full size'}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Add More Photos - Drag & Drop Area */}
                      <div className="mt-6">
                        <div 
                          className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 hover:border-purple-400 transition-colors cursor-pointer"
                          onClick={() => fileUploadRef.current?.click()}
                          onDragOver={(e) => { 
                            e.preventDefault(); 
                            e.currentTarget.classList.add('border-purple-500', 'bg-purple-50'); 
                          }}
                          onDragLeave={(e) => { 
                            e.preventDefault(); 
                            e.currentTarget.classList.remove('border-purple-500', 'bg-purple-50'); 
                          }}
                          onDrop={(e) => { 
                            e.preventDefault();
                            e.currentTarget.classList.remove('border-purple-500', 'bg-purple-50');
                            const files = Array.from(e.dataTransfer.files).filter(file => 
                              file.type.startsWith('image/')
                            );
                            if (files.length > 0) handlePhotoUpload(files);
                          }}
                        >
                          <PhotoIcon className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                          <p className="text-sm font-medium text-gray-600 mb-1">Drop more photos here or click to upload</p>
                          <p className="text-xs text-gray-500">Drag and drop additional project photos</p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div 
                      className="text-center py-12 text-gray-400 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 hover:border-pink-400 transition-colors cursor-pointer"
                      onClick={() => fileUploadRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-pink-500', 'bg-pink-50'); }}
                      onDragLeave={(e) => { e.currentTarget.classList.remove('border-pink-500', 'bg-pink-50'); }}
                      onDrop={(e) => { 
                        e.preventDefault();
                        e.currentTarget.classList.remove('border-pink-500', 'bg-pink-50');
                        const files = Array.from(e.dataTransfer.files);
                        if (files.length > 0) handlePhotoUpload(files);
                      }}
                    >
                      <PhotoIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-lg font-medium text-gray-600 mb-1">Drop photos here or click to upload</p>
                      <p className="text-sm text-gray-500">Drag and drop general project photos or click to browse</p>
                    </div>
                  )}
                </div>

                {uploadingPhotos && (
                  <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                      <div className="text-blue-800 font-medium">Uploading photos...</div>
                    </div>
                    <div className="mt-2 w-full bg-blue-200 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{width: '60%'}}></div>
                    </div>
                    <p className="text-blue-600 text-sm text-center mt-2">
                      Please wait while your photos are being uploaded to the cloud
                    </p>
                  </div>
                )}
                
                {/* Hidden file input for click upload */}
                <input
                  ref={fileUploadRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      handlePhotoUpload(Array.from(e.target.files));
                    }
                  }}
                  style={{ display: 'none' }}
                />
              </div>
            </div>

            {/* Tasks Section */}
            <div className="bg-white rounded-2xl shadow-xl border border-blue-100 p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <CheckCircleIcon className="w-7 h-7 text-blue-600 mr-3" />
                  <h2 className="text-2xl font-bold text-gray-900">Project Tasks</h2>
                </div>
                <Link
                  to={`/projects/${id}/tasks`}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <CheckCircleIcon className="w-4 h-4 mr-2" />
                  Go to Tasks
                </Link>
              </div>
              
              {tasks.length > 0 ? (
                <div className="space-y-4">
                  {tasks.map(task => (
                    <div key={task.id} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">{task.name}</h3>
                          {task.description && (
                            <p className="text-gray-600 mb-3">{task.description}</p>
                          )}
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              task.status === 'completed' ? 'bg-green-100 text-green-700' :
                              task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {task.status?.replace('_', ' ') || 'pending'}
                            </span>
                            {task.budget && (
                              <span>Budget: ${task.budget.toLocaleString()}</span>
                            )}
                            {task.estimatedDays && (
                              <span>Est. {task.estimatedDays} days</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          <Link
                            to={`/projects/${id}/tasks/${task.id}/quotes`}
                            className="inline-flex items-center px-3 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors"
                          >
                            View Quotes
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-xl">
                  <CheckCircleIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No tasks yet</h3>
                  <p className="text-gray-500">Break this project into manageable tasks by clicking "Add Task" above</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Customer Information */}
            <div className="bg-white rounded-2xl shadow-xl border border-green-100 p-6">
              <div className="flex items-center mb-4">
                <UserIcon className="w-6 h-6 text-green-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Customer</h3>
              </div>
              
              <div className="space-y-4">
                <div>
                  <div className="text-xl font-bold text-gray-900">
                    {project.customer?.name || 'No customer assigned'}
                  </div>
                  {project.customer?.company && (
                    <div className="flex items-center text-gray-600 mt-1">
                      <BuildingOfficeIcon className="w-4 h-4 mr-2" />
                      {project.customer.company}
                    </div>
                  )}
                </div>
                
                {project.customer?.email && (
                  <div className="flex items-center text-gray-600">
                    <EnvelopeIcon className="w-4 h-4 mr-2" />
                    <a 
                      href={`mailto:${project.customer.email}`}
                      className="text-pink-600 hover:text-pink-700 transition-colors"
                    >
                      {project.customer.email}
                    </a>
                  </div>
                )}
                
                {project.customer?.phone && (
                  <div className="flex items-center text-gray-600">
                    <PhoneIcon className="w-4 h-4 mr-2" />
                    <a 
                      href={`tel:${project.customer.phone}`}
                      className="text-pink-600 hover:text-pink-700 transition-colors"
                    >
                      {project.customer.phone}
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Project Timeline */}
            <div className="bg-white rounded-2xl shadow-xl border border-orange-100 p-6">
              <div className="flex items-center mb-4">
                <CalendarIcon className="w-6 h-6 text-orange-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Timeline</h3>
              </div>
              
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-500">Start Date</div>
                  <div className="font-semibold text-gray-900">
                    {formatDate(project.startDate)}
                  </div>
                </div>
                
                {project.expectedEndDate && (
                  <div>
                    <div className="text-sm text-gray-500">Expected End</div>
                    <div className="font-semibold text-gray-900">
                      {formatDate(project.expectedEndDate)}
                    </div>
                  </div>
                )}
                
                <div>
                  <div className="text-sm text-gray-500">Created</div>
                  <div className="font-semibold text-gray-900">
                    {formatDate(project.createdAt)}
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl shadow-xl border border-purple-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              
              <div className="space-y-3">
                <Link
                  to={`/projects/${id}/tasks`}
                  className="block w-full px-4 py-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <div className="font-medium">Go to Tasks</div>
                  <div className="text-xs text-blue-600">Create and organize project tasks</div>
                </Link>
                
                <Link
                  to={`/customer/project/${id}/timeline`}
                  className="block w-full px-4 py-3 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                >
                  <div className="font-medium">Customer Timeline</div>
                  <div className="text-xs text-green-600">View customer-facing progress</div>
                </Link>
                
                <button className="block w-full px-4 py-3 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors text-left">
                  <div className="font-medium">Update Status</div>
                  <div className="text-xs text-orange-600">Change project status</div>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Communications Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Project Communications</h2>
          <CommunicationsInbox
            userType="pfi" // TODO: Get from auth context
            userId="current-user-id" // TODO: Get from auth context
            projectId={id}
            userName="PFI Team" // TODO: Get from auth context
            userEmail="team@pinkflamingointeriors.com.au" // TODO: Get from auth context
          />
        </div>

        {/* Photo Lightbox Modal */}
        {selectedPhoto && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedPhoto(null)}
          >
            <div className="relative max-w-4xl max-h-full">
              <button
                onClick={() => setSelectedPhoto(null)}
                className="absolute top-4 right-4 bg-white bg-opacity-20 text-gray-900 rounded-full p-2 hover:bg-opacity-30 transition-all z-10"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
              <img
                src={selectedPhoto.url}
                alt={selectedPhoto.caption || 'Project photo'}
                className="max-w-full max-h-full object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
              {selectedPhoto.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-4 rounded-b-lg">
                  <p className="text-center">{selectedPhoto.caption}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Component Name Modal */}
      {showNameModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Name Your Component</h3>
              <p className="text-gray-600 mb-4">Give this component a descriptive name to help identify it in your project</p>
              <input
                type="text"
                value={newComponentName}
                onChange={(e) => setNewComponentName(e.target.value)}
                placeholder="e.g., Living Room Sofa, Master Bedroom Curtains, etc."
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-pink-500 focus:outline-none text-lg"
                autoFocus
                onKeyPress={(e) => e.key === 'Enter' && handleNameModalConfirm()}
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
              <button
                onClick={handleNameModalCancel}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 focus:outline-none"
              >
                Cancel
              </button>
              <button
                onClick={handleNameModalConfirm}
                disabled={!newComponentName.trim()}
                className="px-6 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue to Smart Wizard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Component Smart Quote Wizard Modal */}
      {showComponentWizard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="w-full max-w-4xl max-h-[90vh] overflow-auto">
            <SmartQuoteWizard
              onComplete={handleComponentWizardComplete}
              onCancel={handleComponentWizardCancel}
              editingComponent={editingComponent}
              componentName={newComponentName}
              projectId={id}
              componentId={currentComponentId}
            />
          </div>
        </div>
      )}

      {/* Milestone Manager Modal */}
      {showMilestoneManager && (
        <MilestoneManager
          projectId={id}
          milestones={milestones}
          onClose={() => setShowMilestoneManager(false)}
          onUpdate={handleMilestoneUpdate}
        />
      )}

      {/* Flamingo Photo Upload Loader */}
      {showFlamingoLoader && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4">
            <FlamingoLoader 
              message={flamingoMessage}
              key={flamingoKey}
            />
          </div>
        </div>
      )}
    </AppLayout>
  );
}